import React from "react";
import Image from "next/image";

import type { ItemType } from "@shared/graphql.types";
import { Link } from "components/Link";
import { StrikethroughPrice } from "@shared/ui.components";

import { useUser } from "lib/user";
import {
  container,
  detailsWrapper,
  wrapper,
  details,
  productSize,
  imageWrapper,
  productShade,
  productPrices,
  numbersWrapper,
  quantityWrapper
} from "./SimpleProductCard.css";
import { useStrikethrough } from "@shared/ui.hooks";
import { Typography } from "@shared/ui.primitives";
import { chooseMembershipSavingsCopy } from "packages/acquisition/shared/components/ProductCardWrapper/utils/chooseMembershipSavingsCopy";

type Props = {
  item: ItemType;
  href: string;
};

export const SimpleProductCardV2: React.FC<Props> = ({ item, href }) => {
  const { hasMembership, hasMembershipInBag } = useUser();
  const { promoPrice, price, guestPrice, traditionalRetailPrice } =
    useStrikethrough(item?.variant?.prices ?? undefined, hasMembership);

  const firstMedia = item?.variant?.detailsMedia || item?.variant?.media?.[0];

  const imageUrl =
    firstMedia?.thumbnails?.size_240 ||
    firstMedia?.thumbnails?.size_OTF_BETA ||
    "";

  const imageAlt = firstMedia?.description || "Beautypie Product";

  const imageTitle = firstMedia?.description || "Beautypie Product";

  const membershipSavingsCopy = chooseMembershipSavingsCopy(
    hasMembership,
    hasMembershipInBag,
    guestPrice,
    traditionalRetailPrice
  );

  return (
    <div data-testid="simple-product-card" className={container}>
      <div className={wrapper}>
        <SimpleProductCardImage
          href={href}
          imageUrl={imageUrl}
          imageAlt={imageAlt}
          imageTitle={imageTitle}
        />

        <div className={detailsWrapper}>
          <div className={details}>
            <Link href={href} variant="acuminMedium" mt="0" mb="4px">
              {item.variant?.brandName}
            </Link>

            <Link
              href={href}
              mt={0}
              variant={
                !item.variant?.brandName ? "acuminMedium" : "acuminLight"
              }
            >
              {item?.variant?.title}
            </Link>

            {item?.variant?.subTitle && (
              <p className={productShade}>{item.variant.subTitle}</p>
            )}

            <p className={productSize}>{item?.variant?.size}</p>
          </div>
          <div className={numbersWrapper}>
            <p className={quantityWrapper}>Qty {item.quantity}</p>
            <div className={productPrices}>
              <StrikethroughPrice
                variant="basketCard"
                price={price?.formatted ?? ""}
                discountedPrice={promoPrice?.formatted ?? ""}
                dataTestId="atb-confirmation-horizontal-product-card"
              />
              {(hasMembership || hasMembershipInBag) &&
                membershipSavingsCopy && (
                  <Typography
                    variant={"smallprint-md"}
                    color={"primary-medium"}
                  >
                    {membershipSavingsCopy}
                  </Typography>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

type CardImageProps = {
  href: string;
  imageUrl: string;
  imageAlt: string;
  imageTitle: string;
};

const SimpleProductCardImage: React.FC<CardImageProps> = ({
  href,
  imageUrl,
  imageAlt,
  imageTitle
}) => {
  return (
    <Link href={href}>
      <div className={imageWrapper}>
        <Image
          src={imageUrl}
          alt={imageAlt}
          title={imageTitle}
          fill
          sizes="(max-width: 768px) 5.6rem, (max-width: 1200px) 6rem"
          style={{
            objectFit: "cover",
            objectPosition: "center"
          }}
        />
      </div>
    </Link>
  );
};
